import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ArrowLeft, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { IncidentTimeline, IncidentBanner, MaintenanceNotice } from '../components';
import {
  getPublicEndpoints,
  getPublicIncidents,
  getPublicIncidentById,
  getPublicMaintenance,
} from '../../../shared/api/publicApi';
import { getSettings } from '../../../shared/api/authApi';
import { Footer } from '../../../shared/components';
import { INCIDENT_STATUS_LABELS } from '../../../shared/constants/status';
import { getOverallStatus } from '../../../shared/utils/status';
import { formatDuration } from '../../../shared/utils/format';
import '../../../styles/base/App.css';
import '../../../styles/features/incidents/IncidentPage.css';

function IncidentPage() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [incident, setIncident] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [siteTitle, setSiteTitle] = useState('Status Page');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching incident data for ID:', incidentId);
      
      const [incidentRes, endpointsRes, incidentsRes, maintenanceRes, settingsRes] = await Promise.all([
        getPublicIncidentById(incidentId),
        getPublicEndpoints(),
        getPublicIncidents(),
        getPublicMaintenance(),
        getSettings().catch(() => ({ data: { title: 'Status Page' } })),
      ]);
      
      console.log('Incident data loaded:', incidentRes.data);
      
      setIncident(incidentRes.data);
      const epData = endpointsRes.data;
      setEndpoints(Array.isArray(epData) ? epData : epData?.endpoints || []);
      const incData = incidentsRes.data;
      setIncidents(Array.isArray(incData) ? incData : []);
      setMaintenance(maintenanceRes.data);
      if (settingsRes.data?.title) setSiteTitle(settingsRes.data.title);
      setError(null);
    } catch (err) {
      console.error('Failed to load incident:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      setError('Failed to load incident details');
    } finally {
      setLoading(false);
    }
  };

  const activeIncidents = incidents.filter(i => !i.resolved_at);

  const duration = incident ? formatDuration(incident.start_time, incident.resolved_at) : 'Ongoing';
  const isResolved = incident?.resolved_at != null;

  return (
    <div className="app">
      <div className="container">
        <div className="header">
          <h1>{siteTitle}</h1>
          <div className="header-actions">
            <button onClick={toggleTheme} className="theme-toggle-btn" title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={() => navigate('/')} className="back-btn">
              <ArrowLeft size={14} /> Back to Status
            </button>
          </div>
        </div>

        <div className="card status-overview">
          <h2>System Status: <strong>{getOverallStatus(endpoints)}</strong></h2>
          <p className="last-updated">Last updated: {new Date().toLocaleTimeString()}</p>
        </div>

        <MaintenanceNotice windows={maintenance} />
        <IncidentBanner incidents={activeIncidents} />

        {loading && (
          <div className="card incident-state-card">
            <p className="incident-state-card__text">Loading incident details...</p>
          </div>
        )}

        {error && !loading && (
          <div className="card incident-state-card">
            <p className="incident-state-card__error">{error}</p>
            <div className="incident-state-card__actions">
              <button onClick={fetchData} className="back-btn">
                Retry
              </button>
              <button onClick={() => navigate('/')} className="back-btn">
                Return to Status Page
              </button>
            </div>
          </div>
        )}

        {!loading && !error && incident && (
          <div className="card incident-detail-card">
          <div className="incident-detail-header">
            <div className="incident-detail-status">
              <span className={`incident-status-badge status-badge-${incident.status}`}>
                {INCIDENT_STATUS_LABELS[incident.status] || incident.status}
              </span>
              {incident.source === 'auto' && (
                <span className="incident-pill pill-auto">Auto-detected</span>
              )}
              <span className={`duration-badge ${isResolved ? 'duration-badge--resolved' : 'duration-badge--active'}`}>
                {duration}
              </span>
            </div>

            <h1 className="incident-detail-title">{incident.title}</h1>

            <div className="incident-detail-meta">
              <div className="incident-detail-time">
                {isResolved && incident.resolved_at ? (
                  <>
                    <strong>Started:</strong> {incident.start_time ? `${format(parseISO(incident.start_time), 'MMMM d, yyyy')} at ${format(parseISO(incident.start_time), 'h:mm a')}` : 'Unknown'}
                    <br />
                    <strong>Resolved:</strong> {format(parseISO(incident.resolved_at), 'MMMM d, yyyy')} at {format(parseISO(incident.resolved_at), 'h:mm a')}
                  </>
                ) : (
                  <>
                    <strong>Started:</strong> {incident.start_time ? `${format(parseISO(incident.start_time), 'MMMM d, yyyy')} at ${format(parseISO(incident.start_time), 'h:mm a')}` : 'Unknown'}
                  </>
                )}
              </div>

              {incident.endpoints && incident.endpoints.length > 0 && (
                <div className="affected-services">
                  <strong>Affected Services:</strong>
                  <div className="service-tags">
                    {incident.endpoints.map(ep => (
                      <span key={ep.id} className="service-tag">{ep.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="incident-detail-timeline">
            <h2>Incident Timeline</h2>
            <IncidentTimeline 
              updates={incident.updates || []} 
              postMortem={incident.post_mortem} 
            />
          </div>
        </div>
        )}

        <Footer />
      </div>
    </div>
  );
}

export default IncidentPage;
